import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, ArrowsClockwise, CreditCard, Star } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProductCard } from '@/components/product/ProductCard';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PRODUCTS, getBestSellers, getNewArrivals } from '@/data/products';

const categories = [
  { name: 'Wall Art', image: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600', slug: 'Wall Art' },
  { name: 'Decorative Lamps', image: 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=600', slug: 'Decorative Lamps' },
  { name: 'Throw Pillows', image: 'https://images.unsplash.com/photo-1629949009765-40fc74c9ec21?w=600', slug: 'Throw Pillows' },
  { name: 'Vases & Planters', image: 'https://images.unsplash.com/photo-1612871177858-b5defed7c67c?w=600', slug: 'Vases & Planters' },
  { name: 'Home Fragrances', image: 'https://images.unsplash.com/photo-1602028915047-37269d1a73f7?w=600', slug: 'Home Fragrances' },
  { name: 'Table Decor', image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600', slug: 'Table Decor' },
  { name: 'Mirrors', image: 'https://images.unsplash.com/photo-1618220179428-22790b461013?w=600', slug: 'Mirrors' },
];

const trustSignals = [
  { icon: ShieldCheck, title: 'Premium Quality', description: 'Handpicked materials for lasting beauty' },
  { icon: Truck, title: 'Fast Shipping', description: 'Free delivery across USA on orders $75+' },
  { icon: ArrowsClockwise, title: 'Easy Returns', description: '30-day hassle-free return policy' },
  { icon: CreditCard, title: 'Secure Checkout', description: 'Your payment information is safe' },
];

const reviews = [
  { name: 'Sarah M.', location: 'New York, NY', rating: 5, text: 'The wall decor looks amazing in my living room. Great quality and fast shipping!' },
  { name: 'Michael R.', location: 'Los Angeles, CA', rating: 5, text: 'Beautiful home decor items. The quality exceeded my expectations.' },
  { name: 'Emily K.', location: 'Chicago, IL', rating: 5, text: 'Love everything I ordered! The vases are stunning and the customer service was excellent.' },
];

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  // Use static data
  const bestSellers = getBestSellers().slice(0, 12);
  const newArrivals = getNewArrivals().slice(0, 8);
  const allProducts = PRODUCTS;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    // Simulate API call
    setTimeout(() => {
      toast.success('Successfully subscribed! Check your email for 10% off.');
      setEmail('');
      setSubscribing(false);
    }, 1000);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1920"
            alt="Beautiful living room"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-4">
              Welcome to Nexaorbits
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] text-nexa-charcoal mb-6">
              Beautiful Home Decor to Transform Your Living Space
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-nexa-charcoal/80 mb-8">
              Discover stylish decor pieces designed to bring elegance, warmth, and personality to your home.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link to="/shop">
                <Button className="btn-primary" data-testid="shop-now-btn">
                  Shop Now
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/category/Wall Art">
                <Button variant="outline" className="btn-secondary" data-testid="explore-collections-btn">
                  Explore Collections
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {trustSignals.map((signal, index) => (
                <div key={index} className="flex items-center gap-2">
                  <signal.icon size={20} className="text-nexa-gold" />
                  <span className="text-sm font-medium text-nexa-charcoal">{signal.title}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
              Browse by Category
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
              Featured Collections
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link 
                  to={`/category/${category.slug}`}
                  className="group block relative aspect-[4/5] rounded-lg overflow-hidden"
                  data-testid={`category-${category.slug.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <img 
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nexa-charcoal/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-xl text-white font-medium">{category.name}</h3>
                    <span className="text-white/70 text-sm flex items-center gap-1 mt-1 group-hover:text-nexa-gold transition-colors">
                      Shop Now <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers Section */}
      <section className="py-24 md:py-32 bg-nexa-bg" data-testid="best-sellers-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
                Customer Favorites
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
                Best Selling Products
              </h2>
            </div>
            <Link to="/shop" className="mt-4 md:mt-0">
              <Button variant="ghost" className="text-nexa-charcoal hover:text-nexa-gold" data-testid="view-all-best-sellers">
                View All <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {bestSellers.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="new-arrivals-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
                Just Arrived
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
                New Arrivals
              </h2>
            </div>
            <Link to="/shop?sort=newest" className="mt-4 md:mt-0">
              <Button variant="ghost" className="text-nexa-charcoal hover:text-nexa-gold" data-testid="view-all-new-arrivals">
                View All <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {newArrivals.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Products Section */}
      <section className="py-24 md:py-32 bg-nexa-bg" data-testid="all-products-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
                Our Collection
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
                All Products
              </h2>
            </div>
            <Link to="/shop" className="mt-4 md:mt-0">
              <Button variant="ghost" className="text-nexa-charcoal hover:text-nexa-gold" data-testid="view-all-products">
                View All <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {allProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="py-24 md:py-32 bg-nexa-stone" data-testid="trust-signals-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
              Why Choose Us
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
              Why Customers Love Our Decor
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustSignals.map((signal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="trust-badge bg-white rounded-lg shadow-sm"
              >
                <signal.icon size={40} className="text-nexa-gold mb-4" />
                <h3 className="font-serif text-lg font-semibold text-nexa-charcoal mb-2">{signal.title}</h3>
                <p className="text-sm text-muted-foreground">{signal.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="reviews-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
              Testimonials
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-nexa-charcoal">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-nexa-bg p-8 rounded-lg"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={18} weight="fill" className="text-nexa-gold" />
                  ))}
                </div>
                <p className="text-nexa-charcoal mb-6 italic">"{review.text}"</p>
                <div>
                  <p className="font-semibold text-nexa-charcoal">{review.name}</p>
                  <p className="text-sm text-muted-foreground">{review.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-nexa-charcoal" data-testid="newsletter-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-3">
              Stay Connected
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-white mb-4">
              Get 10% Off Your First Order
            </h2>
            <p className="text-white/70 text-lg mb-8">
              Join our mailing list for exclusive offers and decor inspiration.
            </p>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                required
                data-testid="footer-newsletter-input"
              />
              <Button 
                type="submit" 
                className="h-14 btn-gold whitespace-nowrap"
                disabled={subscribing}
                data-testid="footer-newsletter-submit"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>

            <p className="text-white/40 text-xs mt-4">
              By subscribing, you agree to our Privacy Policy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
